import { Color } from './color';
import { Constant } from './constant';

/**
 * Edge positions for SVG bounding box calculations.
 */
type Edge = 'top' | 'bottom' | 'left' | 'right';

/**
 * Abstract utility class for SVG element manipulation, conversion, and highlighting operations.
 */
export abstract class Svg {
  private constructor() { /* Prevent instantiation */ }

  /**
   * SVG namespace URI for creating SVG elements.
   */
  private static SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

  /**
   * Attribute stamped on every element MAIDR creates (hidden clones, markers,
   * overlay shapes). Distinguishes MAIDR-owned elements from the chart's own
   * live geometry so disposal can remove the former without deleting the latter.
   */
  private static readonly OWNED_ATTRIBUTE = 'data-maidr-owned';

  /**
   * Marks an element as created (and therefore owned) by MAIDR.
   * Public so trace code that clones or synthesizes highlight elements
   * outside this utility can participate in ownership-aware disposal.
   * @param element - The element to mark
   * @returns The same element, for chaining
   */
  public static markOwned<T extends SVGElement>(element: T): T {
    element.setAttribute(this.OWNED_ATTRIBUTE, 'true');
    return element;
  }

  /**
   * Returns true if the element was created by MAIDR (safe to remove on
   * disposal). Original chart elements referenced for in-place highlighting
   * are not owned and must never be removed.
   * @param element - The element to check
   */
  public static isOwned(element: Element): boolean {
    return element.hasAttribute(this.OWNED_ATTRIBUTE);
  }

  /**
   * Converts an SVG element to a Base64-encoded JPEG data URL.
   * @param svg - The SVG element to convert
   * @returns A promise resolving to the Base64 data URL, or empty string on error
   */
  public static async toBase64(svg: HTMLElement): Promise<string> {
    try {
      // Serialize and optimize SVG
      const svgString = new XMLSerializer()
        .serializeToString(svg)
        .replace(/>\s+</g, '> <') // Safer whitespace handling
        .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
        .trim();

      // Create SVG data URL with proper encoding
      const encodedSVG = encodeURIComponent(svgString)
        .replace(/'/g, '%27')
        .replace(/"/g, '%22');
      const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodedSVG}`;

      // Create and load image
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load SVG image'));
        img.src = svgDataUrl;
      });

      // Create canvas with proper scaling
      const canvas = document.createElement('canvas');
      [canvas.width, canvas.height] = [img.naturalWidth, img.naturalHeight];

      // Draw to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Error converting SVG to Base 64: Canvas context unavailable');
        return '';
      }

      // Convert to JPEG with quality setting
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/jpeg', 0.92);
    } catch (error) {
      console.error('Error converting SVG to Base 64:', error instanceof Error ? error.message : error);
      return '';
    }
  }

  /**
   * Selects all SVG elements matching a query and optionally clones them.
   * @template T - The type of SVG element to select
   * @param query - CSS selector string to query elements
   * @param shouldClone - Whether to clone elements and insert them as hidden copies (default: true)
   * @returns Array of selected (or cloned) SVG elements
   */
  public static selectAllElements<T extends SVGElement>(query: string, shouldClone: boolean = true): T[] {
    return Array
      .from(document.querySelectorAll<T>(query))
      .map((element) => {
        if (!shouldClone) {
          return element;
        }

        const clone = element.cloneNode(true) as T;
        clone.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
        this.markOwned(clone);
        element.insertAdjacentElement(Constant.AFTER_END, clone);
        return clone;
      });
  }

  /**
   * Selects a single SVG element matching a query and optionally clones it.
   * @template T - The type of SVG element to select
   * @param query - CSS selector string to query the element
   * @param shouldClone - Whether to clone the element and insert it as a hidden copy (default: true)
   * @returns The selected (or cloned) SVG element
   */
  public static selectElement<T extends SVGElement>(query: string, shouldClone: boolean = true): T {
    const element = document.querySelector<T>(query);
    if (!shouldClone) {
      return element as T;
    }

    const clone = element?.cloneNode(true) as T;
    clone?.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
    if (clone) {
      this.markOwned(clone);
    }

    element?.insertAdjacentElement(Constant.AFTER_END, clone);
    return clone;
  }

  /**
   * Select the Nth element matching a query, in document order.
   *
   * Useful when a single CSS selector matches multiple sibling elements
   * (e.g. multi-series Vega-Lite line charts where each series renders as
   * a separate `<g class="mark-line role-mark layer_0_marks">` group, all
   * with the same class). CSS `:nth-child(N)` cannot address the Nth
   * matching group when classes differ; this helper does it via JS.
   *
   * Always returns the live DOM element (no cloning side-effects), since
   * the indexed-selection use case is for resolving live DOM nodes that
   * downstream code (highlight pipeline, etc.) operates on directly.
   *
   * @template T - The type of SVG element to select
   * @param query - CSS selector string
   * @param n - Zero-based index into the query results
   * @returns The Nth matching element, or null if no Nth match exists
   */
  public static selectNthElement<T extends SVGElement>(
    query: string,
    n: number,
  ): T | null {
    const all = document.querySelectorAll<T>(query);
    return all[n] ?? null;
  }

  /**
   * Creates an empty, hidden, transparent SVG element of the specified type.
   * @param type - The SVG element type to create (default: 'rect')
   * @returns The newly created SVG element
   */
  public static createEmptyElement(type: string = 'rect'): SVGElement {
    const element = document.createElementNS(this.SVG_NAMESPACE, type) as SVGElement;
    element.setAttribute(Constant.FILL, Constant.TRANSPARENT);
    element.setAttribute(Constant.STROKE, Constant.TRANSPARENT);
    element.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
    return this.markOwned(element);
  }

  /**
   * Creates a circle element styled to match the parent element's stroke or fill.
   * @param cx - The x-coordinate of the circle center
   * @param cy - The y-coordinate of the circle center
   * @param parent - The parent SVG element to inherit styling from
   * @returns The newly created circle element
   */
  public static createCircleElement(cx: string | number, cy: string | number, parent: SVGElement): SVGElement {
    const style = window.getComputedStyle(parent);
    const color = style.stroke || style.fill;
    const strokeWidth = style.strokeWidth || '2';
    const radius = Number.parseFloat(strokeWidth) * 2;
    const element = document.createElementNS(this.SVG_NAMESPACE, Constant.CIRCLE) as SVGElement;

    element.setAttribute(Constant.CIRCLE_X, String(cx));
    element.setAttribute(Constant.CIRCLE_Y, String(cy));
    element.setAttribute(Constant.RADIUS, String(radius));
    element.setAttribute(Constant.FILL, color);
    element.setAttribute(Constant.STROKE, color);
    element.setAttribute(Constant.STROKE_WIDTH, strokeWidth);
    element.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
    this.markOwned(element);

    parent.parentElement?.appendChild(element);
    return element;
  }

  /**
   * Minimum span (in SVG user units) used when getBBox() returns a
   * zero-width or zero-height bounding box — e.g. a vertical `<path>`
   * line drawn for the IQ range inside a violin plot.
   */
  private static readonly MIN_LINE_SPAN = 10;

  /**
   * Creates a line element along a specified edge of an SVG element's bounding box.
   *
   * When the bounding box has zero width (vertical path) or zero height
   * (horizontal path), a minimum span is used so the resulting line is visible.
   *
   * @param box - The SVG element to create a line along
   * @param edge - The edge position ('top', 'bottom', 'left', or 'right')
   * @returns The newly created line element
   */
  public static createLineElement(box: SVGElement, edge: Edge): SVGElement {
    const svg = box as SVGGraphicsElement;
    const bBox = svg.getBBox();

    // Ensure non-zero dimensions so edge lines are always visible.
    // A vertical <path> (width=0) needs a minimum width for top/bottom edges;
    // a horizontal <path> (height=0) needs a minimum height for left/right edges.
    const effectiveWidth = bBox.width || this.MIN_LINE_SPAN;
    const effectiveHeight = bBox.height || this.MIN_LINE_SPAN;
    const cx = bBox.x + bBox.width / 2;
    const cy = bBox.y + bBox.height / 2;

    let x1: number, y1: number, x2: number, y2: number;
    switch (edge) {
      case 'top':
        if (bBox.width === 0) {
          [x1, y1, x2, y2] = [cx - effectiveWidth / 2, bBox.y, cx + effectiveWidth / 2, bBox.y];
        } else {
          [x1, y1, x2, y2] = [bBox.x, bBox.y, bBox.x + bBox.width, bBox.y];
        }
        break;
      case 'bottom':
        if (bBox.width === 0) {
          [x1, y1, x2, y2] = [cx - effectiveWidth / 2, bBox.y + bBox.height, cx + effectiveWidth / 2, bBox.y + bBox.height];
        } else {
          [x1, y1, x2, y2] = [bBox.x, bBox.y + bBox.height, bBox.x + bBox.width, bBox.y + bBox.height];
        }
        break;
      case 'left':
        if (bBox.height === 0) {
          [x1, y1, x2, y2] = [bBox.x, cy - effectiveHeight / 2, bBox.x, cy + effectiveHeight / 2];
        } else {
          [x1, y1, x2, y2] = [bBox.x, bBox.y, bBox.x, bBox.y + bBox.height];
        }
        break;
      case 'right':
        if (bBox.height === 0) {
          [x1, y1, x2, y2] = [bBox.x + bBox.width, cy - effectiveHeight / 2, bBox.x + bBox.width, cy + effectiveHeight / 2];
        } else {
          [x1, y1, x2, y2] = [bBox.x + bBox.width, bBox.y, bBox.x + bBox.width, bBox.y + bBox.height];
        }
        break;
    }

    const style = window.getComputedStyle(box);
    const line = document.createElementNS(this.SVG_NAMESPACE, Constant.LINE) as SVGElement;
    line.setAttribute(Constant.X1, String(x1));
    line.setAttribute(Constant.Y1, String(y1));
    line.setAttribute(Constant.X2, String(x2));
    line.setAttribute(Constant.Y2, String(y2));
    line.setAttribute(Constant.STROKE, style.stroke);
    line.setAttribute(Constant.STROKE_WIDTH, style.strokeWidth || '2');
    line.setAttribute(Constant.VISIBILITY, Constant.HIDDEN);
    this.markOwned(line);

    box.insertAdjacentElement(Constant.AFTER_END, line);
    return line;
  }

  /**
   * Minimum opacity value for fill to be considered visible.
   */
  private static readonly MIN_VISIBLE_FILL_OPACITY = 0.01;
  /**
   * Minimum opacity value for stroke to be considered visible.
   */
  private static readonly MIN_VISIBLE_STROKE_OPACITY = 0.01;
  /**
   * Amount to increase stroke width for highlighting line elements.
   */
  private static readonly STROKE_WIDTH_HIGHLIGHT_INCREASE = 2;

  /**
   * Adjusts opacity values to ensure visibility, returning '1' if below threshold.
   * @param value - The opacity value string to adjust
   * @param minThreshold - The minimum threshold for visibility
   * @returns Adjusted opacity value as a string
   */
  private static getAdjustedOpacity(value: string | null, minThreshold: number): string {
    const parsed = value ? Number.parseFloat(value) : Number.NaN;
    if (!Number.isNaN(parsed) && parsed > minThreshold) {
      return parsed.toString();
    }
    return '1';
  }

  /**
   * Creates a highlighted clone of an SVG element with enhanced visibility.
   *
   * When the element has a zero-size bounding box (e.g. a single-point
   * ``<path d="M x y">`` used for median markers in violin plots), a
   * visible ``<circle>`` is created at that position instead.
   *
   * @param element - The SVG element to highlight
   * @param fallbackColor - Color to use if original color cannot be determined
   * @returns The highlighted clone element
   */
  public static createHighlightElement(element: SVGElement, fallbackColor: string): SVGElement {
    // Handle zero-size elements (e.g. single-point <path> for median markers
    // in violin plots). Create a circle marker instead of cloning.
    try {
      const bbox = (element as SVGGraphicsElement).getBBox();
      if (bbox.width === 0 && bbox.height === 0) {
        const style = window.getComputedStyle(element);
        const strokeWidth = Number.parseFloat(style.strokeWidth || '2');
        const radius = Math.max(strokeWidth * 1.5, 4);
        const circle = document.createElementNS(this.SVG_NAMESPACE, 'circle') as SVGElement;
        circle.setAttribute('cx', String(bbox.x));
        circle.setAttribute('cy', String(bbox.y));
        circle.setAttribute('r', String(radius));
        circle.setAttribute(Constant.FILL, fallbackColor);
        circle.setAttribute(Constant.STROKE, fallbackColor);
        circle.setAttribute(Constant.STROKE_WIDTH, '2');
        circle.setAttribute(Constant.VISIBILITY, Constant.VISIBLE);
        this.markOwned(circle);
        element.insertAdjacentElement(Constant.AFTER_END, circle);
        return circle;
      }
    } catch {
      // getBBox may fail for elements not in the DOM; fall through to normal path.
    }

    const clone = this.markOwned(element.cloneNode(true) as SVGElement);
    const tag = element.tagName.toLowerCase();
    const isLineElement = tag === Constant.POLYLINE || tag === Constant.LINE;

    const computed = window.getComputedStyle(element);
    const originalColor = isLineElement
      ? computed.getPropertyValue(Constant.STROKE)
      : computed.getPropertyValue(Constant.FILL);
    const color = this.getHighlightColor(originalColor, fallbackColor);

    const fillOpacity = computed.getPropertyValue('fill-opacity');
    const strokeOpacity = computed.getPropertyValue('stroke-opacity');
    clone.style.fillOpacity = this.getAdjustedOpacity(fillOpacity, this.MIN_VISIBLE_FILL_OPACITY);
    clone.style.strokeOpacity = this.getAdjustedOpacity(strokeOpacity, this.MIN_VISIBLE_STROKE_OPACITY);

    clone.setAttribute(Constant.VISIBILITY, Constant.VISIBLE);
    clone.setAttribute(Constant.STROKE, color);
    clone.setAttribute(Constant.FILL, color);
    clone.style.fill = color;
    clone.style.stroke = color;

    if (isLineElement) {
      const strokeWidth = window.getComputedStyle(clone).getPropertyValue(Constant.STROKE_WIDTH);
      const match = strokeWidth.match(/^([0-9.]+)([a-z%]*)$/i);
      if (match) {
        const value = Number.parseFloat(match[1]);
        const unit = match[2] || '';
        clone.setAttribute(Constant.STROKE_WIDTH, `${value + this.STROKE_WIDTH_HIGHLIGHT_INCREASE}${unit}`);
      } else {
        const parsed = Number.parseFloat(strokeWidth);
        const value = Number.isNaN(parsed)
          ? this.STROKE_WIDTH_HIGHLIGHT_INCREASE
          : parsed + this.STROKE_WIDTH_HIGHLIGHT_INCREASE;
        clone.setAttribute(Constant.STROKE_WIDTH, `${value}`);
      }
    }

    element.insertAdjacentElement(Constant.AFTER_END, clone);
    return clone;
  }

  /**
   * Determines an appropriate highlight color based on the original color's luminance.
   * @param originalColor - The original color to base the highlight on
   * @param fallbackColor - Color to use if original cannot be parsed or is dark
   * @returns The computed highlight color string
   */
  private static getHighlightColor(originalColor: string, fallbackColor: string): string {
    const originalRgb = Color.parse(originalColor);
    if (!originalRgb) {
      return fallbackColor;
    }

    const contrastWithWhite = Color.getContrastRatio(originalRgb, Constant.HIGHLIGHT_BASE_COLOR);
    const isLight = contrastWithWhite < Constant.HIGHLIGHT_CONTRAST_RATIO;

    // For dark colors, just use the fallback color
    if (!isLight) {
      return fallbackColor;
    }

    const modifiedRgb = { ...originalRgb };

    // Check if the color is grayscale (R=G=B)
    if (originalRgb.r === originalRgb.g && originalRgb.g === originalRgb.b) {
      // For grayscale, modify all channels uniformly
      modifiedRgb.r = Math.min(Constant.HIGHLIGHT_MAX_COLOR, Math.floor(originalRgb.r * Constant.HIGHLIGHT_COLOR_RATIO));
      modifiedRgb.g = Math.min(Constant.HIGHLIGHT_MAX_COLOR, Math.floor(originalRgb.g * Constant.HIGHLIGHT_COLOR_RATIO));
      modifiedRgb.b = Math.min(Constant.HIGHLIGHT_MAX_COLOR, Math.floor(originalRgb.b * Constant.HIGHLIGHT_COLOR_RATIO));
    } else {
      // For non-grayscale colors, modify only the dominant channel
      if (originalRgb.r >= originalRgb.g && originalRgb.r >= originalRgb.b) {
        modifiedRgb.r = Math.min(Constant.HIGHLIGHT_MAX_COLOR, Math.floor(originalRgb.r * Constant.HIGHLIGHT_COLOR_RATIO));
      } else if (originalRgb.g >= originalRgb.r && originalRgb.g >= originalRgb.b) {
        modifiedRgb.g = Math.min(Constant.HIGHLIGHT_MAX_COLOR, Math.floor(originalRgb.g * Constant.HIGHLIGHT_COLOR_RATIO));
      } else {
        modifiedRgb.b = Math.min(Constant.HIGHLIGHT_MAX_COLOR, Math.floor(originalRgb.b * Constant.HIGHLIGHT_COLOR_RATIO));
      }
    }

    return Color.rgbToString(modifiedRgb);
  }

  /**
   * Calculates a contrasting color (black or white) based on the element's fill color.
   * @param element - The SVG element to analyze
   * @returns '#000' for light backgrounds, '#fff' for dark backgrounds
   */
  public static getContrastingColorForElement(element: SVGElement): string {
    const fill = window.getComputedStyle(element).fill || 'rgb(255,255,255)';
    const rgb = Color.parse(fill);
    if (!rgb)
      return '#000';
    const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return luminance > 0.5 ? '#000' : '#fff';
  }

  /**
   * Applies CSS outline styling to highlight a subplot element.
   * @param element - The SVG element to highlight
   * @param color - The color for the outline
   */
  public static setSubplotHighlightCss(element: SVGElement, color: string): void {
    element.style.outline = `4px solid ${color}`;
    element.style.outlineOffset = '3px';
    element.style.borderRadius = '3px';
    element.style.overflow = 'visible';
  }

  /**
   * Removes CSS outline highlighting from a subplot element.
   * @param element - The SVG element to remove highlighting from
   */
  public static removeSubplotHighlightCss(element: SVGElement): void {
    element.style.removeProperty('outline');
    element.style.removeProperty('outline-offset');
    element.style.removeProperty('border-radius');
    element.style.removeProperty('overflow');
  }

  /**
   * Applies SVG stroke highlighting to a subplot with adaptive color based on background.
   * @param group - The SVG group element to highlight
   * @param fallbackColor - Color to use if background color cannot be determined
   * @param figureBgElement - Optional background element to inherit color from
   */
  public static setSubplotHighlightSvgWithAdaptiveColor(group: SVGElement, fallbackColor: string, figureBgElement?: SVGElement): void {
    const bg = group.querySelector('rect, path') as SVGElement | null;
    let originalColor = '';
    if (bg) {
      originalColor = window.getComputedStyle(bg).getPropertyValue('fill');
      if (!originalColor || originalColor === 'none' || originalColor === 'transparent' || originalColor === 'rgba(0, 0, 0, 0)') {
        if (figureBgElement) {
          originalColor = window.getComputedStyle(figureBgElement).getPropertyValue('fill');
        } else {
          originalColor = fallbackColor;
        }
      }
      const highlightColor = this.getHighlightColor(originalColor, fallbackColor);
      // Stash the element's original stroke attributes before overwriting
      // so removeSubplotHighlightSvg can restore them instead of stripping
      // any pre-existing attribute-based border. Guard against re-saving on
      // repeated highlight calls, which would otherwise capture the
      // highlight values as the "original".
      if (!bg.hasAttribute('data-maidr-orig-stroke')) {
        bg.setAttribute('data-maidr-orig-stroke', bg.getAttribute('stroke') ?? '');
        bg.setAttribute('data-maidr-orig-stroke-width', bg.getAttribute('stroke-width') ?? '');
      }
      bg.setAttribute('stroke', highlightColor);
      bg.setAttribute('stroke-width', '4');
    }
  }

  /**
   * Removes SVG stroke highlighting from a subplot element.
   * @param group - The SVG group element to remove highlighting from
   */
  public static removeSubplotHighlightSvg(group: SVGElement): void {
    const bg = group.querySelector('rect, path') as SVGElement | null;
    if (!bg) {
      return;
    }
    // Restore the original stroke attributes saved when the highlight was
    // applied. Only elements we highlighted carry the data- markers; for
    // those, re-apply the saved value if one existed, otherwise remove the
    // attribute we added. Elements we never highlighted are left untouched
    // so their own attribute-based borders survive.
    if (bg.hasAttribute('data-maidr-orig-stroke')) {
      const origStroke = bg.getAttribute('data-maidr-orig-stroke') ?? '';
      if (origStroke === '') {
        bg.removeAttribute('stroke');
      } else {
        bg.setAttribute('stroke', origStroke);
      }
      bg.removeAttribute('data-maidr-orig-stroke');
    }
    if (bg.hasAttribute('data-maidr-orig-stroke-width')) {
      const origStrokeWidth = bg.getAttribute('data-maidr-orig-stroke-width') ?? '';
      if (origStrokeWidth === '') {
        bg.removeAttribute('stroke-width');
      } else {
        bg.setAttribute('stroke-width', origStrokeWidth);
      }
      bg.removeAttribute('data-maidr-orig-stroke-width');
    }
  }
}
